import * as React from "react";

import { cn } from "@/lib/utils";
import Spinner from "@/components/Spinner";
import Tooltip from "@/components/Tooltip";

/**
 * A single figure with its label, and optionally the explanatory tooltip the
 * dashboard already used.
 *
 * Home repeated this shape a dozen times as `<h4 class="p-0 m-0"><span
 * class="text-bold">` plus a `d-flex flex-inline` label row, each tile spelling
 * it out again with small differences in markup.
 *
 * Numbers are grouped by thousands and compacted past 10 000 (12.9K), in the
 * user's language. Deliberately *not* `tabular-nums`: that gives every digit the
 * width of a zero, which reads loose at display sizes. Tabular figures are for
 * columns that must align, not for a standalone value.
 */
const formatValue = (value) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return value;
  const locale = { es: "es", pt: "pt", en: "en" }[localStorage.getItem("language")] || "en";
  return Math.abs(value) >= 10000
    ? new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 }).format(value)
    : new Intl.NumberFormat(locale).format(value);
};

const StatTile = ({ value, label, info, delta, hint, loading = false, children, className }) => {
  if (loading) return <Spinner type={1} />;

  return (
    <div className={cn("flex w-full flex-col gap-1.5", className)}>
      {/* Label first and small, value second and large: the eye lands on the
          figure, and the label is there to name it, not to compete. */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1">
          <p className="m-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          {info && <Tooltip info={info} />}
        </div>

        {delta != null && delta.value != null && (
          <span
            className={cn(
              "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
              // positiveIsGood: false para métricas donde bajar es bueno (tiempos, pendientes)
              delta.value === 0
                ? "bg-muted text-muted-foreground"
                : (delta.value > 0) === (delta.positiveIsGood !== false)
                  ? "bg-success/12 text-success"
                  : "bg-destructive/12 text-destructive"
            )}
          >
            {delta.value > 0 ? "+" : ""}
            {formatValue(delta.value)}
            {delta.suffix}
          </span>
        )}
      </div>

      <p className="m-0 text-2xl font-semibold leading-none text-foreground">
        {formatValue(value)}
      </p>

      {hint && <p className="m-0 text-[11px] text-muted-foreground">{hint}</p>}

      {children}
    </div>
  );
};

/** The same tile when there is nothing to count — label only, no figure. */
const StatTileEmpty = ({ label, info, className }) => (
  <div className={cn("flex items-center justify-center gap-1 text-center", className)}>
    <p className="m-0 text-sm text-muted-foreground">{label}</p>
    {info && <Tooltip info={info} />}
  </div>
);

export { StatTile, StatTileEmpty };
